<template>
  <div
    id="parents-view"
    class="pa-4"
  >
    <view-page-header :show-team-select="false" />

    <section class="parents-view__parents mb-6">
      <v-card class="rounded-lg">
        <v-card-title>
          <div class="subtitle-2">
            {{ $t('card_title.all_parents') }}
          </div>
          <v-spacer />
        </v-card-title>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="tableHeaders"
            :items="parents.data"
            hide-default-footer
            :items-per-page="parents.pagination.per_page"
          >
            <!--<editor-fold desc="ITEM SLOT">-->
            <template
              v-slot:item="{ item: parent }"
            >
              <tr>
                <td>
                  <div class="d-flex align-center">
                    {{ parent.first_name }} {{ parent.last_name }}
                  </div>
                </td>
                <td>
                  <span v-if="parent.email">
                    {{ parent.email }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    N/A
                  </span>
                </td>
                <td>
                  <span v-if="parent.players.length > 0">
                    <v-chip
                      v-for="player in parent.players"
                      :key="parent.id + player.id"
                      label
                      outlined
                      small
                      :to="{ name: $routeNames.PLAYER_DETAILS, params: { id: player.id }}"
                    >
                      {{ player.first_name }} {{ player.last_name }}
                    </v-chip>
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    N/A
                  </span>
                </td>
                <td>
                  <span v-if="parent.user">
                    {{ parent.user.username }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    N/A
                  </span>
                </td>
                <td>
                  <div v-if="parent.user">
                    <v-chip
                      v-if="parent.user.is_setup_complete"
                      small
                      color="success"
                    >
                      {{ $t('status.account_setup.complete') }}
                    </v-chip>
                    <v-chip
                      v-else-if="parent.user.is_invitation_expired"
                      small
                      color="error"
                    >
                      {{ $t('status.account_setup.expired') }}
                      <v-icon
                        x-small
                        class="ml-2"
                      >
                        far fa-triangle-exclamation
                      </v-icon>
                    </v-chip>
                    <v-chip
                      v-else
                      small
                    >
                      {{ $t('status.account_setup.pending') }}
                    </v-chip>
                  </div>
                  <span
                    v-else
                    class="grey--text"
                  >
                    N/A
                  </span>
                </td>
                <td class="caption">
                  <div v-if="parent.user && parent.user.last_mobile">
                    <v-chip
                      small
                      label
                      outlined
                      class="text-truncate mr-2"
                    >
                      <div
                        style="max-width: 150px"
                        class="text-truncate"
                      >
                        {{ parent.user.last_mobile.name }}
                      </div>
                    </v-chip>
                    <span>
                      {{ $dayjs(parent.user.last_mobile.last_used_at).fromNow() }}
                    </span>
                  </div>
                  <div v-else>
                    {{ $t('ultrax_mobile_used.not_used') }}
                  </div>
                </td>
                <td style="width: 0;">
                  <v-btn
                    icon
                    @click="editParent(parent)"
                  >
                    <v-icon small>
                      fal fa-edit
                    </v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
            <!--</editor-fold>-->
            <!--<editor-fold desc="PROGRESS SLOT">-->
            <template
              v-if="parents.loading"
              v-slot:body
            >
              <tbody>
                <tr
                  v-for="i in parents.pagination.per_page"
                  :key="i"
                >
                  <td class="d-flex align-center">
                    <div class="d-inline-flex align-center">
                      <v-skeleton-loader
                        type="text"
                        width="100px"
                      />
                    </div>
                  </td>
                  <td>
                    <v-skeleton-loader
                      type="text"
                      max-width="60px"
                    />
                  </td>
                  <td>
                    <v-skeleton-loader
                      type="text"
                      max-width="60px"
                    />
                  </td>
                  <td>
                    <v-skeleton-loader
                      type="text"
                      max-width="60px"
                    />
                  </td>
                  <td>
                    <v-skeleton-loader
                      type="text"
                      max-width="60px"
                    />
                  </td>
                  <td>
                    <v-skeleton-loader
                      type="text"
                      max-width="60px"
                    />
                  </td>
                  <td />
                </tr>
              </tbody>
            </template>
            <!--</editor-fold>-->
          </v-data-table>
          <div
            v-if="parents.pagination && parents.pagination.last_page > 1"
            class="d-flex justify-center align-center py-5"
          >
            <pagination
              v-model="parents.pagination.current_page"
              :last-page="parents.pagination.last_page"
            />
          </div>
        </v-card-text>
      </v-card>
    </section>
  </div>
</template>

<script>
import { ParentApiService } from '@/data/services'
import debounce from 'lodash/debounce'
import ViewPageHeader from '@/components/ViewPageHeader'
import { reactive } from '@vue/composition-api'
import { EventBus } from '@/plugins/bus'
import Pagination from '@/components/Pagination.vue'
import i18n from '@/plugins/i18n'

export default {
  name: 'AdminParentsView',
  components: { Pagination, ViewPageHeader },
  setup(props) {
    const parents = reactive({
      data: [],
      loading: false,
      pagination: {
        current_page: 1,
        last_page: undefined,
        per_page: 25
      }
    })

    const fetchParents = debounce(async function() {
      try {
        parents.loading = true

        const queryParams = {
          page: parents.pagination.current_page,
          per_page: parents.pagination.per_page
        }

        const resp = await ParentApiService.getPaginated(queryParams)

        parents.data = resp.data
        parents.pagination = resp.meta
      } catch (e) {
        parents.data = []
        parents.pagination.current_page = 1
        parents.pagination.last_page = 1
      } finally {
        parents.loading = false
      }
    }, 500)

    const editParent = function (parent) {
      EventBus.$emit(this.$events.EDIT_PARENT, parent)
    }

    return {
      parents,
      fetchParents,
      editParent
    }
  },
  computed: {
    tableHeaders() {
      return [
        { value: 'name', text: i18n.t('table_headers.name'), sortable: false },
        { value: 'email', text: i18n.t('table_headers.admin_staff.email'), sortable: false },
        { value: 'players', text: i18n.t('table_title.players'), sortable: false },
        { value: 'user.username', text: i18n.t('table_headers.admin_all_players.username'), sortable: false },
        { value: 'user.is_setup_complete', text: i18n.t('table_headers.admin_all_players.account_setup'), sortable: false },
        { value: 'mobile', text: i18n.t('table_headers.admin_all_players.ultrax_mobile_used'), sortable: false },
        { sortable: false }
      ]
    }

  },
  watch: {
    'parents.pagination.current_page': function (newVal, oldVal) {
      this.fetchParents()
    }
  },
  created() {
    this.parents.loading = true

    this.fetchParents()

    EventBus.$on(this.$events.PARENT_CREATED, () => this.fetchParents())
    EventBus.$on(this.$events.PARENT_UPDATED, () => this.fetchParents())
    EventBus.$on(this.$events.PARENT_DELETED, () => this.fetchParents())
  }
}
</script>
